import AsyncStorage from '@react-native-async-storage/async-storage';
import * as Crypto from 'expo-crypto';

export const CHECKOUT_INTENT_KEY = 'flowsellai.billing.checkout-intent.v1';

const PENDING_INTENT_MAX_AGE_MS = 24 * 60 * 60 * 1000;
const SELF_SERVE_PLANS = new Set(['starter', 'growth', 'professional', 'business']);
const BILLING_CYCLES = new Set(['monthly', 'yearly']);
const IDEMPOTENCY_KEY_PATTERN = /^[A-Za-z0-9._:-]{8,128}$/;

export type CheckoutIntentPlan = 'starter' | 'growth' | 'professional' | 'business';
export type CheckoutIntentCycle = 'monthly' | 'yearly';

export interface CheckoutIntent {
  version: 1;
  plan: CheckoutIntentPlan;
  cycle: CheckoutIntentCycle;
  idempotencyKey: string;
  createdAt: string;
  organizationId?: string;
  attemptId?: string;
  attemptExpiresAt?: string;
}

function createIdempotencyKey(): string {
  const value = Array.from(Crypto.getRandomBytes(16), (byte) => byte.toString(16).padStart(2, '0'));
  return `checkout-${value.join('')}`;
}

function isCheckoutIntent(value: unknown): value is CheckoutIntent {
  if (!value || typeof value !== 'object') return false;
  const intent = value as Record<string, unknown>;
  const createdAt =
    typeof intent.createdAt === 'string' ? Date.parse(intent.createdAt) : Number.NaN;

  return (
    intent.version === 1 &&
    typeof intent.plan === 'string' &&
    SELF_SERVE_PLANS.has(intent.plan) &&
    typeof intent.cycle === 'string' &&
    BILLING_CYCLES.has(intent.cycle) &&
    typeof intent.idempotencyKey === 'string' &&
    IDEMPOTENCY_KEY_PATTERN.test(intent.idempotencyKey) &&
    Number.isFinite(createdAt) &&
    (intent.organizationId === undefined || typeof intent.organizationId === 'string') &&
    (intent.attemptId === undefined || typeof intent.attemptId === 'string') &&
    (intent.attemptExpiresAt === undefined ||
      (typeof intent.attemptExpiresAt === 'string' &&
        Number.isFinite(Date.parse(intent.attemptExpiresAt))))
  );
}

export function createCheckoutIntent(
  plan: CheckoutIntentPlan,
  cycle: CheckoutIntentCycle,
  now = new Date(),
): CheckoutIntent {
  return {
    version: 1,
    plan,
    cycle,
    idempotencyKey: createIdempotencyKey(),
    createdAt: now.toISOString(),
  };
}

export async function saveCheckoutIntent(intent: CheckoutIntent): Promise<void> {
  await AsyncStorage.setItem(CHECKOUT_INTENT_KEY, JSON.stringify(intent));
}

export async function getCheckoutIntent(now = new Date()): Promise<CheckoutIntent | null> {
  const stored = await AsyncStorage.getItem(CHECKOUT_INTENT_KEY);
  if (!stored) return null;

  let value: unknown;
  try {
    value = JSON.parse(stored);
  } catch {
    await AsyncStorage.removeItem(CHECKOUT_INTENT_KEY);
    return null;
  }

  if (!isCheckoutIntent(value)) {
    await AsyncStorage.removeItem(CHECKOUT_INTENT_KEY);
    return null;
  }

  const isStalePendingIntent =
    !value.attemptId && now.getTime() - Date.parse(value.createdAt) > PENDING_INTENT_MAX_AGE_MS;
  if (isStalePendingIntent) {
    await AsyncStorage.removeItem(CHECKOUT_INTENT_KEY);
    return null;
  }

  return value;
}

export async function bindCheckoutAttempt(
  intent: CheckoutIntent,
  attempt: { organizationId: string; attemptId: string; expiresAt: string },
): Promise<CheckoutIntent> {
  const boundIntent: CheckoutIntent = {
    ...intent,
    organizationId: attempt.organizationId,
    attemptId: attempt.attemptId,
    attemptExpiresAt: attempt.expiresAt,
  };
  await saveCheckoutIntent(boundIntent);
  return boundIntent;
}

export async function clearCheckoutIntent(expectedIdempotencyKey?: string): Promise<void> {
  if (!expectedIdempotencyKey) {
    await AsyncStorage.removeItem(CHECKOUT_INTENT_KEY);
    return;
  }

  const current = await getCheckoutIntent();
  if (current?.idempotencyKey === expectedIdempotencyKey) {
    await AsyncStorage.removeItem(CHECKOUT_INTENT_KEY);
  }
}
